import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
	Box,
	Typography,
} from '@mui/material'
import { useAuth } from '../contexts/auth-context'
import { supabase } from '../lib/supabase'
import { useAllCustomersSensors } from '../hooks/use-all-customers-sensors'
import HistoricalTab from '../components/HistoricalTab'
import { PageContainer } from '../components/PageContainer'
import { buildCfgSearchParams, buildAllScopeCfgSearchParams, resolveSensorIdFromUrl } from '../utils/sensor-url-params'

export function HistoricalPage() {
	const { customerId, isAllScope } = useAuth()
	const [searchParams, setSearchParams] = useSearchParams()

	// ── Single-customer state ────────────────────────────────────────────────
	const [sensors, setSensors] = useState([])
	const [sensorId, setSensorId] = useState(null)
	const [availableFields, setAvailableFields] = useState({
		alkalinity_ppm: false,
		ph: false,
		temperature: false,
		conductivity: false,
	})
	const [loading, setLoading] = useState(true)

	// ── All-scope state ──────────────────────────────────────────────────────
	const { allSensorsList, loading: allLoading } = useAllCustomersSensors({
		enabled: isAllScope,
		includeLatest: false,
	})
	const [allScopeSensorId, setAllScopeSensorId] = useState(null)
	const [allScopeCustomerId, setAllScopeCustomerId] = useState(null)

	// ── Single-customer: fetch sensors ───────────────────────────────────────
	useEffect(() => {
		if (isAllScope || !customerId) return

		const fetchSensors = async () => {
			const { data } = await supabase
				.from('sensor_configs')
				.select('id, sensor_id, sensor_location, show_alkalinity, show_ph, show_temperature, show_conductivity')
				.eq('customer_id', customerId)
				.eq('delivery_enabled', true)
				.eq('realtime_enabled', true)
				.order('sensor_location')

			setSensors(data && data.length > 0 ? data : [])
			setLoading(false)
		}

		fetchSensors()
	}, [customerId, isAllScope])

	// ── Single-customer: sync URL ↔ selected sensor ──────────────────────────
	useEffect(() => {
		if (isAllScope || loading) return
		if (sensors.length === 0) {
			setSensorId(null)
			return
		}

		const { sensorId: resolved, configId } = resolveSensorIdFromUrl(searchParams, sensors)

		if (resolved) {
			setSensorId(resolved)
			if (configId != null) {
				const cfgStr = String(configId)
				if (searchParams.get('cfg') !== cfgStr || searchParams.has('sensor')) {
					setSearchParams(buildCfgSearchParams(configId), { replace: true })
				}
			}
			return
		}

		const first = sensors[0]
		setSensorId(first.sensor_id)
		const cfgStr = String(first.id)
		if (searchParams.get('cfg') !== cfgStr || searchParams.has('sensor')) {
			setSearchParams(buildCfgSearchParams(first.id), { replace: true })
		}
	}, [loading, isAllScope, sensors, searchParams, setSearchParams])

	// ── All-scope: sync URL ↔ selected sensor ────────────────────────────────
	useEffect(() => {
		if (!isAllScope || allLoading) return
		if (allSensorsList.length === 0) {
			setAllScopeSensorId(null)
			setAllScopeCustomerId(null)
			return
		}

		const { sensorId: resolved, configId } = resolveSensorIdFromUrl(searchParams, allSensorsList)

		if (resolved) {
			const row = allSensorsList.find((s) => s.sensor_id === resolved)
			setAllScopeSensorId(resolved)
			setAllScopeCustomerId(row?.customer_id || null)
			if (configId != null && row) {
				const custStr = String(row.customer_id)
				const cfgStr = String(configId)
				if (searchParams.get('cfg') !== cfgStr || searchParams.get('cust') !== custStr) {
					setSearchParams(buildAllScopeCfgSearchParams(configId, row.customer_id), { replace: true })
				}
			}
			return
		}

		const first = allSensorsList[0]
		setAllScopeSensorId(first.sensor_id)
		setAllScopeCustomerId(first.customer_id)
		setSearchParams(buildAllScopeCfgSearchParams(first.id, first.customer_id), { replace: true })
	}, [isAllScope, allLoading, allSensorsList, searchParams, setSearchParams])

	// ── Single-customer: update availableFields when sensor changes ──────────
	useEffect(() => {
		if (isAllScope || !sensorId || sensors.length === 0) return

		const sensor = sensors.find((s) => s.sensor_id === sensorId)
		if (sensor) {
			setAvailableFields({
				alkalinity_ppm: sensor.show_alkalinity,
				ph: sensor.show_ph,
				temperature: sensor.show_temperature,
				conductivity: sensor.show_conductivity,
			})
		}
	}, [isAllScope, sensorId, sensors])

	// ── All-scope: update availableFields when sensor changes ────────────────
	useEffect(() => {
		if (!isAllScope || !allScopeSensorId || allSensorsList.length === 0) return

		const sensor = allSensorsList.find((s) => s.sensor_id === allScopeSensorId)
		if (sensor) {
			setAvailableFields({
				alkalinity_ppm: sensor.show_alkalinity,
				ph: sensor.show_ph,
				temperature: sensor.show_temperature,
				conductivity: sensor.show_conductivity,
			})
		}
	}, [isAllScope, allScopeSensorId, allSensorsList])

	const handleSensorChange = (newSensorId) => {
		if (isAllScope) {
			const row = allSensorsList.find((s) => s.sensor_id === newSensorId)
			setAllScopeSensorId(newSensorId)
			setAllScopeCustomerId(row?.customer_id || null)
			if (row) {
				setSearchParams(buildAllScopeCfgSearchParams(row.id, row.customer_id), { replace: true })
			}
		} else {
			const row = sensors.find((s) => s.sensor_id === newSensorId)
			setSensorId(newSensorId)
			if (row) {
				setSearchParams(buildCfgSearchParams(row.id), { replace: true })
			}
		}
	}

	const activeSensors = isAllScope ? allSensorsList : sensors
	const activeSensorId = isAllScope ? allScopeSensorId : sensorId
	const activeCustomerId = isAllScope ? allScopeCustomerId : customerId
	const isLoading = isAllScope ? allLoading : loading

	if (isLoading) return <></>

	if (activeSensors.length === 0) {
		return (
			<PageContainer>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
					<Typography color="text.secondary">No sensors configured for this account.</Typography>
				</Box>
			</PageContainer>
		)
	}

	return (
		<PageContainer>
			<Box sx={{ mb: 4 }}>
				<Typography variant="h4" fontWeight="bold" gutterBottom>
					Historical Data
				</Typography>
				<Typography variant="body1" color="text.secondary">
					View and analyze historical sensor measurements
				</Typography>
			</Box>

			{activeSensorId && (
				<HistoricalTab
					config={{ customerId: activeCustomerId, sensorId: activeSensorId }}
					availableFields={availableFields}
					sensors={activeSensors}
					selectedSensorId={activeSensorId}
					onSensorChange={handleSensorChange}
					groupSensorsByCustomer={isAllScope}
				/>
			)}
		</PageContainer>
	)
}
